// @ts-strict-ignore
import type React from "react";
import { useTranslation } from "react-i18next";
import type { BriefDetails } from "src/model/model";

interface QuizzDetailsProps {
  data: BriefDetails | Record<string, unknown>;
  mode: "plan" | "recap";
}

const ns = "pages.chat.interactionDetails";

/** Shared renderer for Quizz Text, Yes/No Question, Random Quizz */
const QuizzDetails: React.FC<QuizzDetailsProps> = ({ data, mode }) => {
  const { t } = useTranslation();
  if (!data) return null;

  if (mode === "plan") {
    const bd = data as BriefDetails;
    const hasContent =
      bd.expectedQuestion || bd.expectedCorrectAnswer || bd.expectedAnswerOptions?.length;
    if (!hasContent) return null;

    return (
      <div className="space-y-1 text-[11px] text-gray-700">
        {bd.expectedQuestion && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.question`)} : </span>
            {bd.expectedQuestion}
          </div>
        )}
        {bd.expectedAnswerOptions && bd.expectedAnswerOptions.length > 0 && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} : </span>
            {bd.expectedAnswerOptions.join(" · ")}
          </div>
        )}
        {bd.expectedCorrectAnswer && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.correctAnswer`)} : </span>
            <span className="text-green-700">{bd.expectedCorrectAnswer}</span>
          </div>
        )}
        {bd.expectedDifficulty && (
          <div>
            <span className="font-medium text-gray-500">{t(`${ns}.difficulty`)} : </span>
            {bd.expectedDifficulty}
          </div>
        )}
      </div>
    );
  }

  // Recap mode: renders the FINAL content. Tolerant of two key sets so the
  // same renderer serves GenerationRecap (model-row shape) AND PlanPreview
  // (authoredContent shape) — that's what makes Plan ≡ Recap.
  const d = data as Record<string, unknown>;
  const questionText = d.question_text ?? d.question ?? d.sort_question;
  const isYesNo = typeof d.is_yes_good_answer === "boolean";
  // Quizz Sort: model shape uses `answers`, authoredContent uses `sort_answers`.
  const sortAnswers = (
    Array.isArray(d.answers)
      ? d.answers
      : Array.isArray(d.sort_answers)
      ? d.sort_answers
      : undefined
  ) as Array<{ text?: string; index?: number }> | undefined;
  // Random Quizz pool (both shapes use `questions`).
  const pool = Array.isArray(d.questions)
    ? (d.questions as Array<Record<string, unknown>>)
    : undefined;
  return (
    <div className="space-y-1 text-[11px] text-gray-700">
      {questionText !== null && questionText !== undefined && questionText !== "" && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.question`)} : </span>
          {String(questionText)}
        </div>
      )}
      {/* Quizz Text single-choice (quizz-1) */}
      {d.answer_1_text && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} :</span>
          <ul className="ml-3 mt-0.5 list-disc list-inside">
            {[1, 2, 3].map((n) => {
              const text = d[`answer_${n}_text`];
              const isCorrect = d[`is_answer_${n}_true`];
              if (!text) return null;
              return (
                <li key={n} className={isCorrect ? "text-green-700" : "text-gray-600"}>
                  {String(text)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* Quizz Text multi-choice (quizz-3) */}
      {Array.isArray(d.list_answers) && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} :</span>
          <ul className="ml-3 mt-0.5 list-disc list-inside">
            {(d.list_answers as string[]).map((ans, i) => {
              const isCorrect = Array.isArray(d.correct_answers)
                ? (d.correct_answers as boolean[])[i]
                : false;
              return (
                <li key={i} className={isCorrect ? "text-green-700" : "text-gray-600"}>
                  {ans}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* Yes/No Question — highlight the correct side */}
      {isYesNo && (
        <ul className="ml-3 mt-0.5 list-disc list-inside">
          <li className={d.is_yes_good_answer ? "text-green-700" : "text-gray-600"}>
            {String(d.yes_button_text || "Oui")}
          </li>
          <li className={!d.is_yes_good_answer ? "text-green-700" : "text-gray-600"}>
            {String(d.no_button_text || "Non")}
          </li>
        </ul>
      )}
      {/* Random Quizz — the pool of questions sampled at runtime */}
      {pool && (
        <div className="space-y-1">
          {pool.map((q, qi) => {
            const qText = (q.question_text ?? q.text) as string | undefined;
            const answers = Array.isArray(q.answers)
              ? (q.answers as Array<Record<string, unknown>>)
              : [];
            return (
              <div key={qi}>
                {qText && <div className="font-medium text-gray-600">{String(qText)}</div>}
                <ul className="ml-3 list-disc list-inside">
                  {answers.map((a, ai) => (
                    <li key={ai} className={a.is_right ? "text-green-700" : "text-gray-600"}>
                      {String(a.text ?? "")}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {/* Quizz Sort — items in canonical order */}
      {sortAnswers && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.answerOptions`)} : </span>
          {sortAnswers
            .slice()
            .sort((a, b) => (a.index || 0) - (b.index || 0))
            .map((a) => a.text || "")
            .join(" → ")}
        </div>
      )}
      {d.explanation_true_text && (
        <div>
          <span className="font-medium text-gray-500">{t(`${ns}.explanationCorrect`)} : </span>
          {String(d.explanation_true_text)}
        </div>
      )}
    </div>
  );
};

export default QuizzDetails;
